import { motion } from "framer-motion";
import { Copy, FileText, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { COLLAB, SITE, SOCIALS } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];
const ICONS = { LinkedIn: Linkedin, "Resume / CV": FileText };

const copyEmail = async () => {
    try {
        await navigator.clipboard.writeText(SITE.email);
        toast.success("Email address copied to clipboard!");
    } catch {
        toast.error("Couldn't copy — the address is below.");
    }
};

export default function Contact() {
    return (
        <section
            id="contact"
            className="border-t border-[#E5E1D8]/70 bg-[#F4F0E8]/40"
        >
            <div className="mx-auto max-w-4xl px-6 py-24 sm:px-10 sm:py-32">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8C8B88]"
                >
                    contact
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
                    className="mt-6 max-w-2xl font-serif text-3xl leading-[1.15] tracking-tight text-[#141413] sm:text-5xl"
                >
                    Let's build something{" "}
                    <span className="italic text-[#C84B31]">thoughtful</span>{" "}
                    together.
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
                    className="mt-6 max-w-xl text-base leading-relaxed text-[#6E6D6A] sm:text-lg"
                >
                    Whether it's a development, an investment, or a hard
                    question about a business that deserves a closer look — my
                    inbox is always open.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
                    data-testid="collab-block"
                    className="mt-12"
                >
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C8B88]">
                        ways to work together
                    </p>
                    <div className="mt-4 grid gap-px overflow-hidden rounded-xl border border-[#E5E1D8] bg-[#E5E1D8]/70 sm:grid-cols-3">
                        {COLLAB.map((c, i) => (
                            <div
                                key={c.title}
                                className="bg-[#FBF9F5] p-6 transition-colors duration-300 hover:bg-[#F4F0E8]/80"
                            >
                                <p className="font-mono text-[10px] tracking-[0.2em] text-[#C84B31]">
                                    {String(i + 1).padStart(2, "0")}
                                </p>
                                <h3 className="mt-3 font-serif text-lg tracking-tight text-[#141413]">
                                    {c.title}
                                </h3>
                                <p className="mt-1.5 text-sm leading-relaxed text-[#6E6D6A]">
                                    {c.line}
                                </p>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7, delay: 0.18, ease: EASE }}
                    className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-4"
                >
                    <a
                        href={`mailto:${SITE.email}`}
                        data-testid="contact-email-link"
                        className="u-link font-serif text-xl italic text-[#141413] transition-colors duration-300 hover:text-[#C84B31] sm:text-2xl"
                    >
                        {SITE.email}
                    </a>
                    <button
                        onClick={copyEmail}
                        data-testid="contact-email-copy-button"
                        aria-label="Copy email address"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#141413]/20 text-[#141413]/70 transition-colors duration-300 hover:border-[#C84B31] hover:bg-[#C84B31] hover:text-[#FBF9F5]"
                    >
                        <Copy size={14} strokeWidth={1.75} />
                    </button>
                    <a
                        href={SITE.phoneHref}
                        data-testid="contact-phone-link"
                        className="font-mono text-xs tracking-[0.15em] text-[#6E6D6A] transition-colors duration-300 hover:text-[#C84B31]"
                    >
                        {SITE.phone}
                    </a>
                </motion.div>

                <motion.ul
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7, delay: 0.24, ease: EASE }}
                    className="mt-10 flex flex-wrap gap-3"
                >
                    {SOCIALS.map((s) => {
                        const Icon = ICONS[s.name];
                        return (
                            <li key={s.name}>
                                <a
                                    href={s.url}
                                    target={s.url.startsWith("http") ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    data-testid={s.testid}
                                    className="inline-flex items-center gap-2 rounded-full border border-[#141413]/20 px-5 py-2.5 text-sm text-[#141413] transition-colors duration-300 hover:border-[#141413] hover:bg-[#141413] hover:text-[#FBF9F5]"
                                >
                                    <Icon size={15} strokeWidth={1.75} />
                                    {s.name.toLowerCase()}
                                </a>
                            </li>
                        );
                    })}
                </motion.ul>
            </div>
        </section>
    );
}
